
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  HiOutlineBookOpen,
  HiOutlineArrowDown,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineDocumentText,
  HiOutlineSquare3Stack3D,
} from "react-icons/hi2";

import { Header, Fotter } from "../components/layout";
import { Loader, BooksCard } from "../components/utils";

import { getAllBooks } from "../redux/actions/books";
import { BooksCategoryImage } from "../assets";

const PAGE_SIZE = 9;

/* ============================================
   PAGINATION HELPER
============================================ */

function getPageNumbers(current, total) {
  const count = Math.min(total, 5);

  const start = Math.max(
    0,
    Math.min(current - 2, total - count)
  );

  return Array.from(
    { length: count },
    (_, index) => start + index
  );
}

/* ============================================
   ALL BOOKS PAGE
============================================ */

function AllBooks() {
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation();

  const [page, setPage] = useState(0);

  const {
    isLoading,
    Allbooks,
    pagination,
  } = useSelector((state) => state.Books || {});

  const isEnglish = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en");

  useEffect(() => {
    dispatch(getAllBooks(page, PAGE_SIZE));
  }, [dispatch, page]);

  const books = Array.isArray(Allbooks)
    ? Allbooks
    : [];

  const totalPages = Math.max(
    1,
    Number(pagination?.totalPages) || 1
  );

  const visiblePages = getPageNumbers(page, totalPages);

  const content = {
    eyebrow: isEnglish
      ? "AL NOMAN LEGAL PUBLICATIONS"
      : "إصدارات مؤسسة النعمان",

    title: t("Our Publications"),

    description: t(
      "Explore a collection of legal books and writings that we have prepared to serve as a comprehensive reference for you across various legal fields."
    ),

    explore: isEnglish
      ? "Browse Publications"
      : "تصفح الإصدارات",

    imageLabel: isEnglish
      ? "Legal Knowledge & Publications"
      : "المعرفة والمؤلفات القانونية",

    sectionEyebrow: isEnglish
      ? "THE PUBLICATIONS LIBRARY"
      : "مكتبة الإصدارات",

    sectionTitle: isEnglish
      ? "Explore Our Legal Books"
      : "تصفح مؤلفاتنا القانونية",

    sectionDescription: isEnglish
      ? "Discover the available legal publications, explore their subjects, and download the books that interest you."
      : "اطلع على مجموعة الإصدارات القانونية المتاحة، واختر المؤلفات التي تهمك لتحميلها والرجوع إليها.",

    available: isEnglish
      ? "Books on This Page"
      : "كتب في هذه الصفحة",

    emptyTitle: isEnglish
      ? "No Publications Available"
      : "لا توجد إصدارات متاحة",

    emptyDescription: isEnglish
      ? "Legal publications will appear here when they become available."
      : "ستظهر المؤلفات والإصدارات القانونية هنا عند إضافتها.",

    page: isEnglish ? "Page" : "صفحة",
    of: isEnglish ? "of" : "من",

    previous: isEnglish
      ? "Previous Page"
      : "الصفحة السابقة",

    next: isEnglish
      ? "Next Page"
      : "الصفحة التالية",
  };

  /* ======================================
     SCROLL TO BOOKS
  ====================================== */

  const scrollToBooks = () => {
    document
      .getElementById("books-library")
      ?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
  };

  /* ======================================
     PAGINATION
  ====================================== */

  const changePage = (next) => {
    if (
      next < 0 ||
      next >= totalPages ||
      next === page ||
      isLoading
    ) {
      return;
    }

    setPage(next);
    scrollToBooks();
  };

  return (
    <>
      <Header active={6} />

      <main className="min-h-screen overflow-hidden bg-skin-page">

        {/* ======================================
            01 - EDITORIAL HERO
        ====================================== */}

        <section
          className="
            relative isolate overflow-hidden

            bg-[#F4F1EA]
            text-[#19344D]

            dark:bg-[#102536]
            dark:text-white
          "
        >
          {/* Subtle background decoration */}
          <div
            aria-hidden="true"
            className="
              pointer-events-none
              absolute -end-40 -top-48
              h-[550px] w-[550px]
              rounded-full
              bg-[#C19B60]/[0.07]
              blur-[90px]
            "
          />

          <div className="container relative">

            <div
              className="
                grid items-center
                gap-12

                pb-16 pt-28
                md:pb-20 md:pt-32

                lg:min-h-[620px]
                lg:grid-cols-2
                lg:gap-20

                xl:gap-28
              "
            >

              {/* ==================================
                  HERO TEXT
              ================================== */}

              <div className="min-w-0">

                {/* Eyebrow */}
                <div className="mb-7 flex items-center gap-3">

                  <span
                    aria-hidden="true"
                    className="h-px w-10 bg-brand-gold"
                  />

                  <span className="eyebrow text-brand-goldDark dark:text-brand-goldLight">
                    {content.eyebrow}
                  </span>
                </div>

                {/* Main heading */}
                <h1 className="h1 max-w-[680px] text-[#19344D] dark:text-white">
                  {content.title}
                </h1>

                {/* Decorative divider */}
                <div
                  aria-hidden="true"
                  className="mt-7 h-[3px] w-16 rounded-full bg-brand-gold"
                />

                {/* Description */}
                <p
                  className="
                    body-1 mt-7 max-w-[610px]

                    text-[#52697C]
                    dark:text-[#BDCFDB]
                  "
                >
                  {content.description}
                </p>

                {/* Main action */}
                <button
                  type="button"
                  onClick={scrollToBooks}
                  className="
                    group mt-9 inline-flex
                    min-h-[54px]

                    items-center justify-center
                    gap-4

                    rounded-xl
                    border border-[#19344D]
                    bg-[#19344D]

                    px-6 py-3
                    text-white

                    transition-all duration-300

                    hover:-translate-y-0.5
                    hover:bg-[#294961]

                    focus-visible:outline-none
                    focus-visible:ring-2
                    focus-visible:ring-brand-gold

                    dark:border-white/20
                    dark:bg-white/10
                    dark:backdrop-blur-xl
                    dark:hover:bg-white/20
                  "
                >
                  <span className="button-sm">
                    {content.explore}
                  </span>

                  <HiOutlineArrowDown
                    size={20}
                    aria-hidden="true"
                    className="
                      transition-transform duration-300
                      group-hover:translate-y-1
                    "
                  />
                </button>

                {/* Supporting identity */}
                <div
                  className="
                    mt-11 flex items-center gap-4

                    border-t border-[#19344D]/10
                    pt-6

                    dark:border-white/10
                  "
                >
                  <div
                    className="
                      flex h-11 w-11
                      items-center justify-center

                      rounded-xl
                      border border-brand-gold/20
                      bg-brand-gold/10

                      text-brand-goldDark
                      dark:text-brand-goldLight
                    "
                  >
                    <HiOutlineBookOpen
                      size={24}
                      aria-hidden="true"
                    />
                  </div>

                  <span className="body-3 text-[#52697C] dark:text-[#BDCFDB]">
                    {content.imageLabel}
                  </span>
                </div>

              </div>

              {/* ==================================
                  HERO IMAGE
              ================================== */}

              <div className="relative min-w-0">

                {/* Navy decorative background */}
                <div
                  aria-hidden="true"
                  className="
                    absolute -bottom-5 -end-5
                    h-[85%] w-[80%]

                    rounded-[24px]
                    bg-[#19344D]

                    dark:bg-[#294961]
                  "
                />

                {/* Small golden corner */}
                <div
                  aria-hidden="true"
                  className="
                    absolute -start-4 -top-4
                    h-28 w-28

                    border-s-2 border-t-2
                    border-brand-gold/70
                  "
                />

                {/* Image */}
                <div
                  className="
                    group relative
                    overflow-hidden rounded-[22px]

                    border-[6px] border-white
                    bg-[#19344D]

                    shadow-[0_24px_65px_rgba(15,35,54,0.15)]

                    dark:border-[#294961]
                  "
                >
                  <img
                    src={BooksCategoryImage}
                    alt={content.imageLabel}
                    fetchPriority="high"
                    decoding="async"
                    className="
                      aspect-[5/4]
                      w-full object-cover object-center

                      transition-transform duration-700
                      group-hover:scale-105
                    "
                  />

                  <div
                    aria-hidden="true"
                    className="
                      pointer-events-none
                      absolute inset-0

                      bg-gradient-to-t
                      from-[#071522]/75
                      via-transparent
                      to-transparent
                    "
                  />

                  {/* Image caption */}
                  <div className="absolute bottom-6 start-6 end-6">

                    <div className="mb-3 h-[2px] w-10 bg-[#D7B77F]" />

                    <div className="flex items-center gap-3 text-white">

                      <HiOutlineBookOpen
                        size={25}
                        aria-hidden="true"
                        className="text-[#D7B77F]"
                      />

                      <span className="font-kufi text-sm font-semibold sm:text-base">
                        {content.imageLabel}
                      </span>
                    </div>
                  </div>

                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ======================================
            02 - PUBLICATIONS LIBRARY
        ====================================== */}

        <section
          id="books-library"
          aria-labelledby="books-heading"
          className="
            relative scroll-mt-[90px]

            border-t border-[#DBE4EA]
            bg-[#EAF0F4]

            py-16
            md:py-20
            lg:py-24

            dark:border-white/10
            dark:bg-[#0F2435]
          "
        >
          <div className="container">

            {/* ==================================
                SECTION HEADER
            ================================== */}

            <div
              className="
                mb-12 flex flex-col
                justify-between gap-7

                lg:flex-row
                lg:items-end
              "
            >
              <div className="max-w-[750px]">

                <div className="mb-5 flex items-center gap-3">

                  <span className="h-px w-9 bg-brand-gold" />

                  <span className="eyebrow text-brand-goldDark dark:text-brand-goldLight">
                    {content.sectionEyebrow}
                  </span>
                </div>

                <h2
                  id="books-heading"
                  className="section-title text-skin-heading"
                >
                  {content.sectionTitle}
                </h2>

                <p className="body-2 mt-5 text-skin-muted">
                  {content.sectionDescription}
                </p>
              </div>

              {/* Number of loaded books */}
              {!isLoading && books.length > 0 && (
                <div
                  className="
                    inline-flex items-center gap-3
                    self-start

                    rounded-xl
                    border border-skin-border
                    bg-skin-surface
                    px-4 py-3

                    lg:self-auto
                  "
                >
                  <HiOutlineSquare3Stack3D
                    size={22}
                    className="text-skin-accent"
                    aria-hidden="true"
                  />

                  <span className="font-cairo text-lg font-bold tabular-nums text-skin-heading">
                    {books.length}
                  </span>

                  <span className="body-3 text-skin-muted">
                    {content.available}
                  </span>
                </div>
              )}
            </div>

            {/* ==================================
                BOOKS GRID
            ================================== */}

            {isLoading ? (
              <div className="flex min-h-[380px] items-center justify-center">
                <Loader />
              </div>
            ) : books.length > 0 ? (
              <>
                <div
                  className="
                    grid grid-cols-1
                    items-stretch gap-7

                    sm:grid-cols-2
                    xl:grid-cols-3

                    xl:gap-8
                  "
                >
                  {books.map((book, index) => (
                    <BooksCard
                      key={book._id || index}
                      book={book}
                      index={page * PAGE_SIZE + index}
                    />
                  ))}
                </div>

                {/* ==================================
                    PAGINATION
                ================================== */}

                {totalPages > 1 && (
                  <nav
                    aria-label={
                      isEnglish
                        ? "Books pagination"
                        : "ترقيم صفحات الكتب"
                    }
                    className="
                      mt-14 flex flex-col
                      items-center justify-between
                      gap-6

                      border-t border-skin-border
                      pt-9

                      sm:flex-row
                    "
                  >
                    {/* Counter */}
                    <div className="flex items-center gap-2 font-cairo text-sm text-skin-muted">
                      {content.page}

                      <span className="font-bold tabular-nums text-skin-heading">
                        {page + 1}
                      </span>

                      {content.of}

                      <span className="tabular-nums">
                        {totalPages}
                      </span>
                    </div>

                    {/* Buttons */}
                    <div className="flex flex-wrap items-center justify-center gap-2">

                      <button
                        type="button"
                        disabled={page === 0 || isLoading}
                        onClick={() => changePage(page - 1)}
                        aria-label={content.previous}
                        className="
                          flex h-11 w-11
                          items-center justify-center

                          rounded-xl
                          border border-skin-border
                          bg-skin-surface

                          text-skin-heading

                          transition-colors
                          hover:border-skin-accent/50

                          disabled:cursor-not-allowed
                          disabled:opacity-40

                          focus-visible:outline-none
                          focus-visible:ring-2
                          focus-visible:ring-skin-accent
                        "
                      >
                        <HiOutlineChevronLeft
                          size={20}
                          className="rtl:-scale-x-100"
                        />
                      </button>

                      {visiblePages.map((number) => {
                        const active = number === page;

                        return (
                          <button
                            key={number}
                            type="button"
                            disabled={isLoading}
                            onClick={() => changePage(number)}
                            aria-label={`${content.page} ${number + 1}`}
                            aria-current={
                              active ? "page" : undefined
                            }
                            className={`
                              flex h-11 min-w-[44px]
                              items-center justify-center

                              rounded-xl border px-3

                              font-cairo text-sm
                              font-semibold tabular-nums

                              transition-colors

                              ${
                                active
                                  ? "border-[#19344D] bg-[#19344D] text-white dark:border-white/20 dark:bg-white/15"
                                  : "border-skin-border bg-skin-surface text-skin-muted hover:border-skin-accent/50 hover:text-skin-heading"
                              }
                            `}
                          >
                            {String(number + 1).padStart(2, "0")}
                          </button>
                        );
                      })}

                      <button
                        type="button"
                        disabled={
                          page >= totalPages - 1 || isLoading
                        }
                        onClick={() => changePage(page + 1)}
                        aria-label={content.next}
                        className="
                          flex h-11 w-11
                          items-center justify-center

                          rounded-xl
                          border border-skin-border
                          bg-skin-surface

                          text-skin-heading

                          transition-colors
                          hover:border-skin-accent/50

                          disabled:cursor-not-allowed
                          disabled:opacity-40

                          focus-visible:outline-none
                          focus-visible:ring-2
                          focus-visible:ring-skin-accent
                        "
                      >
                        <HiOutlineChevronRight
                          size={20}
                          className="rtl:-scale-x-100"
                        />
                      </button>
                    </div>
                  </nav>
                )}
              </>
            ) : (
              /* Empty state */
              <div
                className="
                  flex min-h-[340px]
                  flex-col items-center justify-center

                  rounded-[24px]
                  border border-skin-border
                  bg-skin-surface

                  px-6 py-12 text-center
                "
              >
                <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-skin-soft text-skin-accent">
                  <HiOutlineDocumentText size={34} />
                </div>

                <h3 className="h5 text-skin-heading">
                  {content.emptyTitle}
                </h3>

                <p className="body-2 mt-4 max-w-[460px] text-skin-muted">
                  {content.emptyDescription}
                </p>
              </div>
            )}

          </div>
        </section>

      </main>

      <Fotter />
    </>
  );
}

export default AllBooks;
